// ── BorrowerGuarantorForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, FInput, FSelect, Field, Modal, ReadOnlyForm, Td, Th } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { US_STATE_NAMES } from "../../../shared/usStates";
import { stateOfOrgState } from "../../../shared/entityIdentity";

// Codes carrying the state name, ordered by name. A bare list of 51
// abbreviations is a list you have to already know in order to use.
const STATE_OPTIONS = [{ value: "", label: "— Select —" }].concat(
  Object.keys(US_STATE_NAMES)
    .sort((a, b) => US_STATE_NAMES[a].localeCompare(US_STATE_NAMES[b]))
    .map(c => ({ value: c, label: c + " — " + US_STATE_NAMES[c] })));

// The same restricted red BankAccountForm uses for the bank credentials and the
// full-numbers reveal. Reusing the tone rather than the app's ordinary cyan link
// colour is the point: this control and that one do the same kind of thing, and
// should look like they do.
const RESTRICTED = "#B03A0F";

export const BorrowerGuarantorForm = ({ initial, accountid, readOnly }) => {
  const { api, role, modal, setModal, closeModal, saving, error, saveRec } = useApp();
  // ── THE TIN IS BLANKED ON OPEN ──
  // useFreshForm re-fetches the row through getRecord and merges it, so
  // whatever getRecord returns lands in form state. That endpoint now projects
  // the TIN down to tinlast4 — but blanking it here as well is the same
  // belt-and-braces BankAccountForm applies to account numbers, and it is what
  // makes "blank = unchanged" true rather than merely conventional: the box
  // starts empty, so saving without touching it cannot overwrite the stored
  // value with a truncated one.
  const [f, set] = useFreshForm(initial ? {
    ...initial,
    tin: "",
  } : {
    accountid, borrowerguarantorname:"", borrowerguarantor:"Borrower",
    entitytype:"LLC", tin:"", stateoforganization:""
  }, "BorrowersGuarantors");

  // ── Revealing the full number ──
  // Offered only to a Portfolio Manager, and only for a saved entity. The
  // client copy of the flag decides whether to OFFER it; api/getTIN re-checks
  // before disclosing anything, and writes an audit row before it reads.
  const [tinFull, setTinFull] = useState(null);
  const [tinErr, setTinErr]   = useState(null);
  const [tinBusy, setTinBusy] = useState(false);
  const revealTin = async () => {
    setTinBusy(true); setTinErr(null);
    try {
      const r = await api.get("getTIN", { id: initial.id });
      // A missing value is said out loud rather than shown as a blank: "no TIN
      // on file" and "the reveal failed" look identical otherwise.
      setTinFull(r && r.tin ? r.tin : "");
    } catch (e) { setTinErr(e.message); }
    setTinBusy(false);
  };
  const roleOptions   = ["","Borrower","Guarantor"];
  const entityOptions = ["","LLC","Individual","Corporation","Limited Partnership"];
  const ST = ({ title }) => <div style={{ gridColumn:"1/-1", fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>{title}</div>;

  // An individual is located at their principal residence rather than a state
  // of organization, so the field is hidden instead of shown-and-ignored. The
  // server refuses a value there, and a disabled box would only raise the
  // question of why it is disabled.
  const isIndividual = f.entitytype === "Individual";

  // Read from the SAVED row, not from the box. Confirming is a statement about
  // the stored value, so the badge has to show the stored value or somebody is
  // agreeing to something they cannot see.
  const saved = stateOfOrgState(initial || {});

  // Editing the state makes any existing confirmation moot — the server clears
  // it on save, because a sign-off was given about a different answer. Saying
  // so before the save beats discovering it afterwards.
  const stateChanged = !!initial
    && String(f.stateoforganization || "").toUpperCase()
       !== String(initial.stateoforganization || "").toUpperCase();

  const noticeStyle = { fontSize:12, color:"#7a5200", background:"#FAEEDA",
                        border:"0.5px solid #EBD3A0", borderRadius:8, padding:"8px 12px" };

  // ── RELATED BANK ACCOUNTS ──
  // The bank accounts held in this entity's name (BankAccounts.borrowerid).
  // Read-only here: getBankAccounts returns the last four only, and adding or
  // editing an account stays on the Bank Accounts tab, where the full-number
  // and credential rules live.
  const [banks, setBanks] = useState(null);
  const [bankErr, setBankErr] = useState(null);
  useEffect(() => {
    if (!initial || !initial.id) return;
    api.get("getBankAccounts", { borrowerid: initial.id })
      .then(r => setBanks(Array.isArray(r) ? r : []))
      .catch(e => { setBanks([]); setBankErr(e.message); });
  }, [initial && initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Modal title={readOnly?"View Borrower/Guarantor":initial?"Edit Borrower/Guarantor":"Add Borrower/Guarantor"} onClose={closeModal} wide
    onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"borrowerguarantor-edit",rec:initial,accountid,onDone:modal.onDone}); }:null}>
    <ReadOnlyForm readOnly={readOnly}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 24px" }}>
        <ST title="Entity" />
        <Field label="Name"><FInput value={f.borrowerguarantorname||""} onChange={set("borrowerguarantorname")} /></Field>
        <Field label="Role"><FSelect value={f.borrowerguarantor||""} onChange={set("borrowerguarantor")} options={roleOptions} /></Field>
        <Field label="Entity Type"><FSelect value={f.entitytype||""} onChange={set("entitytype")} options={entityOptions} /></Field>
        <Field label={initial ? "TIN (blank = unchanged)" : "TIN"}>
          <FInput value={f.tin||""} onChange={set("tin")}
                  placeholder={initial ? (initial.tinlast4 ? "••••• " + initial.tinlast4 : "none on file") : "9 digits"} />
          {initial && (
            <div style={{ fontSize:11, marginTop:4, minHeight:16 }}>
              {tinFull !== null ? (
                <span style={{ fontFamily:"var(--font-mono)", fontSize:12.5, fontWeight:600 }}>
                  {tinFull || "No TIN on file for this entity."}
                </span>
              ) : role.canViewTIN ? (
                <span onClick={tinBusy ? undefined : revealTin}
                      style={{ cursor: tinBusy ? "default" : "pointer", color:RESTRICTED, fontWeight:600,
                               textDecoration:"underline", textDecorationStyle:"dotted" }}>
                  {tinBusy ? "Revealing…" : "Reveal full TIN"}
                </span>
              ) : (
                <span style={{ color:"var(--color-text-secondary)" }}>
                  Full number is restricted to Portfolio Managers.
                </span>
              )}
              {tinErr && <span style={{ color:"#993C1D", marginLeft:8 }}>{tinErr}</span>}
            </div>
          )}
        </Field>

        {!isIndividual && <>
          <ST title="Perfection" />
          <div style={{ gridColumn:"1/3" }}>
            <Field label="State of Organization">
              <FSelect value={f.stateoforganization||""} onChange={set("stateoforganization")}
                       options={STATE_OPTIONS} />
            </Field>
          </div>
          <div style={{ gridColumn:"3/-1", alignSelf:"end", paddingBottom:14 }}>
            {saved.bg && !stateChanged && (
              <span title={saved.note}
                    style={{ fontSize:11.5, background:saved.bg, color:saved.fg, padding:"2px 8px",
                             borderRadius:4, fontWeight:600, whiteSpace:"nowrap" }}>
                {saved.label}
              </span>
            )}
          </div>

          {/* The whole reason this column exists. Every existing row was copied
              from the account's headquarters state, which is right for most
              borrowers and wrong for exactly the ones where it matters — a
              Delaware LLC operating out of North Carolina. Until somebody has
              read the charter the value is a guess, and a guess must not be
              allowed to look like a fact. */}
          {!readOnly && (
            <div style={{ gridColumn:"1/-1", marginTop:-4, marginBottom:8 }}>
              {stateChanged ? (
                <div style={noticeStyle}>
                  Changing the state clears any previous confirmation — that was given about a different
                  answer. Tick the box below if you have checked this one against the entity's charter.
                </div>
              ) : saved.key === "provisional" ? (
                <div style={noticeStyle}>
                  This was copied from the account's headquarters state as a starting point. A company can
                  operate in one state and be registered in another, and a financing statement filed in the
                  wrong one perfects nothing.
                </div>
              ) : null}

              <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:8,
                              fontSize:12.5, cursor: f.stateoforganization ? "pointer" : "default",
                              opacity: f.stateoforganization ? 1 : 0.5 }}>
                <input type="checkbox" disabled={!f.stateoforganization}
                       checked={!!f.confirmstateoforg}
                       onChange={e => set("confirmstateoforg")(e.target.checked)} />
                I have verified this against the entity's charter
              </label>
            </div>
          )}
        </>}
      </div>
    </ReadOnlyForm>
    {initial && initial.id && (
      <div style={{ marginTop:8 }}>
        <ST title={"Bank Accounts" + (banks && banks.length ? " (" + banks.length + ")" : "")} />
        {banks === null ? <div style={{ fontSize:12.5, color:"var(--color-text-secondary)" }}>Loading…</div>
         : bankErr ? <div style={{ fontSize:12.5, color:"#993C1D" }}>Could not load bank accounts: {bankErr}</div>
         : banks.length === 0 ? <div style={{ fontSize:12.5, color:"var(--color-text-secondary)" }}>
             No bank accounts in this entity's name. Add them on the account's Bank Accounts tab.</div>
         : <div style={{ overflowX:"auto" }}>
             <table style={{ width:"100%", borderCollapse:"collapse" }}>
               <thead><tr>
                 <Th>Bank</Th><Th>Account</Th><Th>Description</Th><Th>Lockbox</Th><Th>Control Agmt</Th><Th>Status</Th>
               </tr></thead>
               <tbody>
                 {banks.map(b => (
                   // Clickable only when viewing: in edit mode, opening the bank
                   // account would replace this modal and lose unsaved changes.
                   <tr key={b.id} title={readOnly ? "Open this bank account" : undefined}
                       style={{ cursor: readOnly ? "pointer" : "default" }}
                       onClick={readOnly ? () => { closeModal(); setModal({ type:"bankaccount-view", rec:b }); } : undefined}
                       onMouseEnter={readOnly ? e => e.currentTarget.style.background = "var(--color-background-secondary)" : undefined}
                       onMouseLeave={readOnly ? e => e.currentTarget.style.background = "" : undefined}>
                     <Td style={{ fontWeight:500 }}>
                       {b.bankname || "—"}
                       {b.primaryaccount ? <span style={{ marginLeft:6, fontSize:10.5, fontWeight:600, padding:"1px 6px",
                                                          borderRadius:4, background:B.cyanLight, color:B.cyanDark }}>Primary</span> : null}
                     </Td>
                     <Td style={{ fontFamily:"var(--font-mono)", whiteSpace:"nowrap" }}>{b.accountnumberlast4 ? "••" + b.accountnumberlast4 : "—"}</Td>
                     <Td style={{ fontSize:12.5 }}>{b.description || "—"}</Td>
                     <Td>{b.lockbox ? "Yes" : "No"}</Td>
                     <Td style={{ fontSize:12.5 }}>{b.controlagreement ? (b.controlagreementtype || "Yes") : "No"}</Td>
                     <Td style={{ whiteSpace:"nowrap", fontSize:12.5 }}>
                       {b.accountcloseddate
                         ? <span style={{ color:"var(--color-text-secondary)" }}>Closed {b.accountcloseddate}</span>
                         : <span style={{ color:"#0F6E56", fontWeight:600 }}>Open</span>}
                     </Td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>}
      </div>
    )}
    {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{error}</div>}
    <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
      <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
      {!readOnly&&<Btn disabled={saving} onClick={()=>saveRec("saveBorrowerGuarantor",{...f,accountid:f.accountid||accountid},initial?"Edited":"Added","BorrowersGuarantors",(initial?"Updated":"Added")+" borrower/guarantor",modal.onDone)} variant="primary">{saving?"Saving...":"Save"}</Btn>}
    </div>
  </Modal>;
};
